import { useEffect, useRef } from "react";
import PiCheckTickSingleStroke from "@markeroo/icons/src/stroke/general/PiCheckTickSingleStroke";
import PiDeleteDustbin01Stroke from "@markeroo/icons/src/stroke/general/PiDeleteDustbin01Stroke";
import PiDrawPencilStroke from "@markeroo/icons/src/stroke/editing/PiDrawPencilStroke";
import PiUturnLeftStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiUturnLeftStroke";
import PiCheckTickCircleSolid from "@markeroo/icons/src/solid/general/PiCheckTickCircleSolid";
import { Tooltip } from "@/components/Tooltip";
import type { HighlightWithMetadata } from "../types/highlightNavigation";
import { ui } from "@/strings";

interface HighlightListItemProps {
  highlight: HighlightWithMetadata;
  isSelected: boolean;
  isTeacher: boolean;
  onClick: () => void;
  onToggleComplete?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Whether this resolved highlight can be unresolved (body hash matches) */
  canUnresolve?: boolean;
}

export function HighlightListItem({
  highlight,
  isSelected,
  isTeacher,
  onClick,
  onToggleComplete,
  onEdit,
  onDelete,
  canUnresolve = false,
}: HighlightListItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);

  // Synchronize browser focus with selection state
  useEffect(() => {
    if (isSelected && itemRef.current) {
      // Move browser focus to match selected state
      itemRef.current.focus({ preventScroll: true });
    }
  }, [isSelected]);
  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleComplete?.();
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <div
      ref={itemRef}
      onClick={onClick}
      className={`
        flex flex-col gap-2 p-2.5 sm:p-3 rounded-lg cursor-pointer transition-colors
        focus:outline-none
        ${isSelected ? "bg-surface-page ring-2 ring-primary" : "bg-white hover:bg-surface-secondary"}
        ${highlight.isCompleted && !isSelected ? "opacity-60" : ""}
      `}
      data-highlight-list-item-id={highlight.id}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
        // Tab is handled by useHotkeys at document level
      }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-1.5 sm:gap-2">
        <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
          {/* Color indicator */}
          <div
            className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: highlight.color }}
            aria-hidden="true"
          />

          {/* Label */}
          <span className="text-xs sm:text-sm font-medium text-text-theme-primary truncate">
            {highlight.label}
          </span>

          {/* Completed indicator (for teachers viewing student work) */}
          {isTeacher && highlight.isCompleted && (
            <Tooltip content={ui.highlightNavigation.resolvedTooltip}>
              <span className="relative flex items-center">
                <PiCheckTickCircleSolid
                  size={16}
                  className="text-semantic-success flex-shrink-0"
                  aria-label={ui.highlightNavigation.completedIcon}
                />
              </span>
            </Tooltip>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          {/* Student: Resolve button for unresolved highlights */}
          {!isTeacher && onToggleComplete && !highlight.isCompleted && (
            <button
              onClick={handleToggleComplete}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-text-theme-secondary bg-surface-page hover:bg-semantic-success-bg hover:text-semantic-success-text rounded transition-colors"
              aria-label={ui.highlightNavigation.resolve}
              title={ui.highlightNavigation.markComplete}
            >
              <PiCheckTickSingleStroke size={12} />
              <span className="hidden sm:inline">{ui.highlightNavigation.resolve}</span>
            </button>
          )}

          {/* Student: Unresolve button for resolved highlights (only if body hash matches) */}
          {!isTeacher && onToggleComplete && highlight.isCompleted && canUnresolve && (
            <button
              onClick={handleToggleComplete}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-text-theme-muted bg-surface-page hover:bg-border hover:text-text-theme-secondary rounded transition-colors"
              aria-label={ui.highlightNavigation.unresolve}
              title={ui.highlightNavigation.markIncomplete}
            >
              <PiUturnLeftStroke size={12} />
              <span className="hidden sm:inline">{ui.highlightNavigation.unresolve}</span>
            </button>
          )}

          {/* Student: Disabled Unresolve button when document has changed */}
          {!isTeacher && onToggleComplete && highlight.isCompleted && !canUnresolve && (
            <Tooltip content={ui.highlightNavigation.unresolveDisabledTooltip}>
              <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-border bg-surface-secondary rounded cursor-not-allowed">
                <PiUturnLeftStroke size={12} />
                <span className="hidden sm:inline">{ui.highlightNavigation.unresolve}</span>
              </span>
            </Tooltip>
          )}

          {isTeacher && highlight.type === "ai" && onEdit && (
            <button
              onClick={handleEdit}
              className="p-0.5 sm:p-1 text-text-theme-muted hover:text-primary transition-colors"
              aria-label={ui.highlightNavigation.editHighlight}
              title={ui.highlightNavigation.editHighlight}
            >
              <PiDrawPencilStroke size={16} />
            </button>
          )}

          {isTeacher && onDelete && (
            <button
              onClick={handleDelete}
              className="p-0.5 sm:p-1 text-text-theme-muted hover:text-semantic-error transition-colors"
              aria-label={ui.highlightNavigation.deleteHighlight}
              title={ui.highlightNavigation.deleteHighlight}
            >
              <PiDeleteDustbin01Stroke size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Phrase preview */}
      <p className="text-xs sm:text-sm text-text-theme-secondary line-clamp-2">
        {/* eslint-disable-next-line no-restricted-syntax */}
        <span aria-hidden="true">&ldquo;</span>
        {highlight.phrase}
        {/* eslint-disable-next-line no-restricted-syntax */}
        <span aria-hidden="true">&rdquo;</span>
      </p>

      {/* Explanation (if selected) */}
      {isSelected && highlight.explanation && (
        <p className="text-xs text-text-theme-muted pt-2 border-t border-border">
          {highlight.explanation}
        </p>
      )}
    </div>
  );
}
